import React, { useEffect, useState } from 'react';
import { color } from '@moda/tokens';
import { Breakpoint, Button, Constrain, Divider, Stack, Text } from '@moda/om';
import LogoModaPrivateIcon from '@moda/icons/logo-moda-private-165-x-12';
import { hrefFor } from '../../routers';
import { Link } from '../Link';
import { tc } from '../../lib/trackingContext';
import { SwiperCarousel } from '../Swiper';
import { ModaPrivateRequestModal } from './ModaPrivateRequestModal';

import './ModaPrivate.scss';

export const ModaPrivate: React.FC = () => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [biosArray, setBiosArray] = useState<string[]>([]);
  const [shuffledImageNames, setShuffledImageNames] = useState<string[]>([]);

  const imageNames = [
    'PCA_1.png',
    'PCA_2.png',
    'PCA_3.png',
    'PCA_4.png',
    'PCA_5-1.png',
    'PCA_6.png',
    'PCA_7-1.png',
    'PCA_8.png',
    'PCA_9.png',
    'PCA_10.png',
    'PCA_11.png',
    'PCA_12.png',
    'PCA_13.png'
  ];

  const backgroundColors = [
    color('coral'),
    color('lilac'),
    color('seafoam'),
    color('cornflower-blue'),
    color('ivory')
  ];

  const shuffleBiosArray = (array: string[], currentIndex = 0): string[] => {
    if (currentIndex === array.length - 1) {
      return array;
    }

    const randomIndex = Math.floor(Math.random() * (array.length - currentIndex)) + currentIndex;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];

    return shuffleBiosArray(array, currentIndex + 1);
  };

  useEffect(() => {
    const shuffledNames = shuffleBiosArray(imageNames);
    setShuffledImageNames(shuffledNames);
    const images = shuffledNames.map(imageName => `/dist/public/stylist/${imageName}`);
    setBiosArray(images);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type JoinTheWailtListEvent =
    | 'Banner Image Clicked'
    | 'Banner Image Mobile Clicked'
    | 'Service Section Clicked'
    | 'Team Section Clicked';

  const handleClick = (text: JoinTheWailtListEvent, label: string, location: string) => {
    setShowRequestModal(true);
    tc.track(text, {
      addToTrackingEvent: {
        label: label,
        location: location
      }
    });
  };

  return (
    <>
      <div className="ModaPrivate">
        <Breakpoint gt="sm">
          <div className="ModaPrivate__image-container">
            <img
              className="ModaPrivate__image-main"
              src="/dist/public/stylist/moda_private_v2.jpg"
              alt="Moda Private"
            />
            <Stack space={2}>
              <Stack space={2} className="ModaPrivate__text-main">
                <LogoModaPrivateIcon
                  className="ModaPrivate__logo-icon"
                  height="18px"
                  width="248px"
                />

                <Text treatment="h5" family="serif" color="snow" className="ModaPrivate__title">
                  Our Premier Styling Program
                </Text>
                <Text treatment="body1" color="snow">
                  Enjoy complimentary personal styling, wardrobing, and special VIP invitations as a
                  member of our private client program. Entry to the program is by invitation only
                  with qualifying spend. If you'd like to join, please click the link below.
                </Text>
              </Stack>
              <Button
                className="ModaPrivate__button-main"
                onClick={() =>
                  handleClick('Banner Image Clicked', 'APPLY NOW', 'Banner Image Desktop')
                }
              >
                APPLY NOW
              </Button>
            </Stack>
          </div>
          <Stack className="ModaPrivate__our-service" space={2} alignItems="center">
            <div className="ModaPrivate__section-title">Our SERVICE</div>
            <Text color="ink" treatment="body1">
              Luxury wardrobe and life-styling for the every day and the extraordinary.
            </Text>
          </Stack>
          <Constrain>
            <Stack
              className="ModaPrivate__service-section"
              space={5}
              direction="horizontal"
              alignItems="center"
            >
              <img
                className="ModaPrivate__service-img"
                src="/dist/public/stylist/moda_private_v2_DT_3.jpg"
                alt="Moda Private Services"
                loading="lazy"
              />
              <Stack className="ModaPrivate__service-text" space={5} alignItems="flex-start">
                <img
                  className="ModaPrivate__trademark"
                  src="/dist/public/moda_trademark_black.svg"
                  alt="Moda trademark"
                  width={93}
                  height={27}
                />
                <Stack space={1}>
                  <Text color="ink" family="serif" treatment="h4">
                    Private Shopping
                  </Text>
                  <Text color="ink" treatment="body1">
                    Stylists will curate looks based on your needs with digital lookbooks and
                    personalized edits.
                  </Text>
                </Stack>
                <Stack space={1}>
                  <Text color="ink" family="serif" treatment="h4">
                    Expert Styling
                  </Text>
                  <Text color="ink" treatment="body1">
                    Personal styling sessions tailored to your lifestyle needs (from special events
                    to seasonal wardrobing) and private fittings globally—available in person or
                    online, as preferred.
                  </Text>
                </Stack>
                <Stack space={1}>
                  <Text color="ink" family="serif" treatment="h4">
                    Gifting
                  </Text>
                  <Text color="ink" treatment="body1">
                    We can find the perfect gift for your partner, family member, friend or
                    colleague.
                  </Text>
                </Stack>
                <Stack space={1}>
                  <Text color="ink" family="serif" treatment="h4">
                    Special Sourcing
                  </Text>
                  <Text color="ink" treatment="body1">
                    Bespoke one-of-a-kind items, vintage, couture, fine jewelry, rare decor, and
                    more.
                  </Text>
                </Stack>
                <div className="ModaPrivate__button-wrapper">
                  <Button
                    className="ModaPrivate__button"
                    onClick={() =>
                      handleClick('Service Section Clicked', 'APPLY NOW', 'Service Section Desktop')
                    }
                  >
                    APPLY NOW
                  </Button>
                </div>
              </Stack>
            </Stack>
            <Divider />
            <Stack className="ModaPrivate__team-section" space={6}>
              <Stack space={1} className="ModaPrivate__team-section-text">
                <div className="ModaPrivate__section-title">Meet Our TEAM</div>
                <Text color="ink" treatment="body1" className="ModaPrivate__team-text">
                  Match with one of our Moda Private Client Advisors
                </Text>
              </Stack>
              <SwiperCarousel
                identifier="ModaPrivateTeam"
                slidesPerGroup={3}
                slidesPerView={3}
                spaceBetween={24}
                pagination={{
                  enabled: false
                }}
                circleArrow
              >
                {biosArray.map((imgSrc, index) => (
                  <div
                    key={index}
                    className="ModaPrivate__swiper-image"
                    style={{
                      backgroundColor:
                        backgroundColors[index % backgroundColors.length] ?? undefined
                    }}
                  >
                    <img
                      className="ModaPrivate__stylist-image"
                      key={index}
                      src={imgSrc}
                      alt={shuffledImageNames[index]}
                      loading="lazy"
                    />
                  </div>
                ))}
              </SwiperCarousel>
              <div className="ModaPrivate__button-wrapper">
                <Button
                  className="ModaPrivate__button"
                  onClick={() =>
                    handleClick('Team Section Clicked', 'APPLY NOW', 'Team section Desktop')
                  }
                >
                  APPLY NOW
                </Button>
              </div>
            </Stack>
            <Divider />
          </Constrain>
        </Breakpoint>

        <Breakpoint lt="sm">
          <div className="ModaPrivate__image-container--mobile">
            <img
              className="ModaPrivate__image-main"
              src="/dist/public/stylist/moda_private_v2_mobile.jpg"
              alt="Moda Private"
            />

            <Stack space={4} className="ModaPrivate__text-container" alignItems="center">
              <LogoModaPrivateIcon
                className="ModaPrivate__logo-icon--mobile"
                height="18px"
                width="254px"
              />
              <Stack space={2}>
                <Text treatment="h4" family="serif">
                  Our Premier Styling Program
                </Text>
                <Text treatment="body1">
                  Enjoy complimentary personal styling, wardrobing, and special VIP invitations as a
                  member of our private client program. Entry to the program is by invitation only
                  with qualifying spend. If you'd like to join, please click the link below.
                </Text>
                <div className="ModaPrivate__button-wrapper">
                  <Button
                    secondary
                    className="ModaPrivate__button-main--mobile"
                    onClick={() =>
                      handleClick('Banner Image Mobile Clicked', 'APPLY NOW', 'Banner Image Mobile')
                    }
                  >
                    APPLY NOW
                  </Button>
                </div>
              </Stack>
            </Stack>
          </div>
          <Stack className="ModaPrivate__our-service" space={2} alignItems="center">
            <div className="ModaPrivate__section-title">Our SERVICE</div>
            <Text color="ink" treatment="body1">
              Luxury wardrobe and life-styling for the every day and the extraordinary.
            </Text>
          </Stack>
          <img
            className="ModaPrivate__image-mobile"
            src="/dist/public/stylist/moda_private_MOB_2.jpg"
            alt="Moda Private MOB"
            loading="lazy"
          />

          <Constrain>
            <Stack className="ModaPrivate__service-text--mobile" space={5}>
              <img
                className="ModaPrivate__trademark"
                src="/dist/public/moda_trademark_black.svg"
                alt="Moda trademark"
                width={80}
              />
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h4">
                  Personal Shopping
                </Text>
                <Text color="ink" treatment="body1">
                  Our Private Client Advisors will curate looks based on your needs with digital
                  lookbooks and personalized edits.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h4">
                  Expert Styling
                </Text>
                <Text color="ink" treatment="body1">
                  Personal styling sessions tailored to your lifestyle needs (from special events to
                  seasonal wardrobing) and private fittings globally—available in person or online,
                  as preferred.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h4">
                  Gifting
                </Text>
                <Text color="ink" treatment="body1">
                  Our stylists will find the perfect gift for your partner, family member, friend or
                  colleague.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h4">
                  Special Sourcing
                </Text>
                <Text color="ink" treatment="body1">
                  Bespoke one-of-a-kind items, vintage, couture, fine jewelry, rare decor, and more.
                </Text>
              </Stack>
              <div className="ModaPrivate__button-wrapper">
                <Button
                  secondary
                  className="ModaPrivate__button"
                  onClick={() =>
                    handleClick('Service Section Clicked', 'APPLY NOW', 'Service Section Mobile')
                  }
                >
                  APPLY NOW
                </Button>
              </div>
            </Stack>
            <Divider />
            <Stack className="ModaPrivate__team-section--mobile" space={6}>
              <Stack space={1} className="ModaPrivate__team-section-text">
                <div className="ModaPrivate__section-title">Meet Our TEAM</div>
                <Text color="ink" treatment="body1" className="ModaPrivate__team-text">
                  Match with one of our Moda Private Client Advisors
                </Text>
              </Stack>
              <SwiperCarousel
                identifier="ModaPrivateTeam"
                slidesPerView={1}
                spaceBetween={24}
                pagination={{
                  enabled: false
                }}
                circleArrow
              >
                {biosArray.map((imgSrc, index) => (
                  <div
                    key={index}
                    className="ModaPrivate__stylist-image-wrapper"
                    style={{
                      backgroundColor:
                        backgroundColors[index % backgroundColors.length] ?? undefined
                    }}
                  >
                    <img
                      className="ModaPrivate__stylist-image"
                      key={index}
                      src={imgSrc}
                      alt={shuffledImageNames[index]}
                      loading="lazy"
                    />
                  </div>
                ))}
              </SwiperCarousel>
              <div className="ModaPrivate__button-wrapper">
                <Button
                  secondary
                  className="ModaPrivate__button"
                  onClick={() =>
                    handleClick('Team Section Clicked', 'APPLY NOW', 'Team Section Mobile')
                  }
                >
                  APPLY NOW
                </Button>
              </div>
            </Stack>
            <Divider />
          </Constrain>
        </Breakpoint>

        <Constrain className="ModaPrivate__constrain">
          <Stack space={5} className="ModaPrivate__faq-section">
            <div className="ModaPrivate__section-title">FAQ</div>
            <Stack space={4}>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  How Does Moda Private Work?
                </Text>
                <Text color="ink" treatment="body1">
                  Moda Private connects you with an expert Private Client Advisor who will assist
                  you in any way you need. You'll receive weekly style edits, access to private
                  collections, VIP events invitations, special item sourcing, and more.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  Is There A Fee Associated With Moda Private?
                </Text>
                <Text color="ink" treatment="body1">
                  The service is complimentary once you are paired with a member of our styling
                  team.
                </Text>
              </Stack>

              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  How Will I Hear From My Private Client Advisor?
                </Text>
                <Text color="ink" treatment="body1">
                  However you prefer! Email is our default channel, but your PCA can communicate via
                  text, WhatsApp, FaceTime, etc. If you'd like to be contacted in a specific way,
                  please don't hesitate to let us know.
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Constrain>

        <Breakpoint gt="sm">
          <Stack
            className="ModaPrivate__moda-network"
            alignItems="center"
            space={10}
            direction="horizontal"
          >
            <Stack space={5} className="ModaPrivate__moda-network-text">
              <div className="ModaPrivate__section-title">MODA Network</div>
              <Text color="ink" treatment="body1">
                Are you an independent stylist, interior decorator, or costume designer? Join our
                invitation-only network to receive unique access and commissions to luxury brands
                and designers globally.
              </Text>
              <div className="ModaPrivate__moda-network-button-wrapper">
                <Button to={hrefFor.ModaNetwork()} className="ModaPrivate__moda-network-button">
                  LEARN MORE
                </Button>
              </div>
            </Stack>
            <img
              className="ModaPrivate__network-img"
              src="/dist/public/stylist/moda_private_DT_4A.png"
              alt="Moda Private Network"
              loading="lazy"
            />
          </Stack>
        </Breakpoint>

        <Breakpoint lt="sm">
          <Stack space={1} className="ModaPrivate__moda-network--mobile" alignItems="center">
            <img
              className="ModaPrivate__network-img--mobile"
              src="/dist/public/stylist/moda_private_MOB_4.jpg"
              alt="Moda Private Network"
              loading="lazy"
            />
            <Stack space={4} className="ModaPrivate__moda-network-text--mobile">
              <div className="ModaPrivate__section-title">MODA Network</div>
              <Text color="ink" treatment="body1">
                Are you an independent stylist, interior decorator, or costume designer? Join our
                invitation-only network to receive unique access and commissions to luxury brands
                and designers globally.
              </Text>
              <div className="ModaPrivate__moda-network-button-wrapper">
                <Button to={hrefFor.ModaNetwork()} className="ModaPrivate__moda-network-button">
                  LEARN MORE
                </Button>
              </div>
            </Stack>
          </Stack>
        </Breakpoint>

        <Constrain>
          <Stack space={5} className="ModaPrivate__shop-more-section">
            <div className="ModaPrivate__section-title">Shop MORE</div>
            <Stack space={1} alignItems="center" className="ModaPrivate__links">
              <Link to="/shop/laurens-closet" className="ModaPrivate__link">
                <Text color="ink" treatment="body1" family="serif">
                  LAUREN'S CLOSET: Discover LSD's favorite pieces
                </Text>
              </Link>
              <Link to="/new" className="ModaPrivate__link">
                <Text color="ink" treatment="body1" family="serif">
                  WHAT'S NEW: The Latest From Leading Designer Names
                </Text>
              </Link>
              <Link to="/shop/the-event-edit" className="ModaPrivate__link ModaPrivate__link--last">
                <Text color="ink" treatment="body1" family="serif">
                  THE EVENT EDIT: Exciting Plans? Find Something Fabulous To Wear Here
                </Text>
              </Link>
            </Stack>
          </Stack>
        </Constrain>
      </div>
      <ModaPrivateRequestModal show={showRequestModal} onClose={() => setShowRequestModal(false)} />
    </>
  );
};
